<template>
  <section class="regional-info-section mb-12" v-if="regionais.length > 0">
    <div class="section-container">
      <div class="section-title">
        <h3 class="text-3xl font-bold uppercase text-gray-800 mb-8">
          Candidatos e Documentos por Regional
        </h3>
      </div>

      <div class="regional-filter mb-12 text-center">
        <label
          for="regionalSelect"
          class="block text-gray-700 font-semibold mb-2 text-lg"
          >Selecione uma Regional:</label
        >
        <select
          id="regionalSelect"
          v-model="selectedRegional"
          class="
            form-select
            block
            w-full
            md:w-1/2
            lg:w-1/3
            mx-auto
            mt-1
            p-3
            border border-gray-300
            rounded-md
            shadow-sm
            focus:outline-none focus:ring-lime-500 focus:border-lime-500
            text-base text-gray-700
            bg-white
            cursor-pointer
          "
        >
          <option :value="null">Selecione...</option>
          <option
            v-for="regional in regionais"
            :key="regional.id"
            :value="regional.id"
          >
            {{ regional.nome }}
          </option>
        </select>
      </div>

      <div v-if="selectedRegional" class="regional-content">
        <!-- Mensagem caso ambos estejam vazios -->
        <div
          key="empty-state"
          v-if="
            filteredDiretorios.length === 0 && filteredDocuments.length === 0
          "
          class="
            w-full
            text-center
            p-10
            bg-white
            shadow-sm
            rounded-lg
            border border-gray-200
            mt-6
          "
        >
          <i class="fa fa-folder-open text-4xl text-gray-300 mb-4 block"></i>
          <p class="text-gray-600 text-lg font-medium">
            Nenhum candidato ou documento disponível para a regional
            selecionada.
          </p>
          <p class="text-gray-400 text-sm mt-2">
            Por favor, selecione outra regional no menu acima.
          </p>
        </div>

        <!-- Container Dinâmico -->
        <div
          key="content-state"
          v-else
          :class="[
            'grid grid-cols-1 gap-8 lg:gap-12',
            filteredDiretorios.length > 0 && filteredDocuments.length > 0
              ? 'lg:grid-cols-2'
              : '',
          ]"
        >
          <!-- Coluna Candidatos -->
          <div
            key="col-cand"
            class="w-full"
            v-if="filteredDiretorios.length > 0"
          >
            <div class="mb-6 flex items-center">
              <h3
                class="
                  text-2xl
                  font-bold
                  text-gray-800
                  border-b-2 border-lime-600
                  pb-2
                  uppercase
                  tracking-wide
                "
              >
                Candidatos
              </h3>
            </div>
            <div
              class="overflow-y-auto pr-2 custom-scrollbar"
              style="max-height: 600px"
            >
              <div
                v-for="diretorio in filteredDiretorios"
                :key="diretorio.codigo"
                class="mb-6"
              >
                <div
                  class="
                    bg-gray-50
                    border-l-4 border-amber-600
                    mb-4
                    rounded-r-md
                    p-3
                    shadow-sm
                  "
                >
                  <h4
                    class="
                      font-bold
                      text-sm
                      uppercase
                      text-gray-700
                      tracking-wide
                    "
                  >
                    {{ diretorio.nome }}
                  </h4>
                </div>
                <div
                  v-for="cargo in diretorio.chapas"
                  :key="cargo.id || cargo.codigo || cargo.nome"
                  class="mb-5 ml-2"
                >
                  <div
                    class="
                      bg-gray-100
                      border-l-4 border-lime-600
                      mb-3
                      rounded-r-md
                      p-2
                    "
                  >
                    <h5 class="font-bold text-sm text-gray-800">
                      {{ "0" + cargo.numero + " - " + cargo.nome }}
                    </h5>
                  </div>
                  <ul
                    :class="[
                      'grid gap-3 ml-2',
                      filteredDiretorios.length > 0 &&
                      filteredDocuments.length > 0
                        ? 'grid-cols-1'
                        : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
                    ]"
                  >
                    <li
                      v-for="(candidato, index) in cargo.candidatos"
                      :key="candidato.cpf || candidato.nome || index"
                    >
                      <div
                        class="
                          flex
                          bg-white
                          shadow-sm
                          hover:shadow
                          transition-shadow
                          duration-200
                          rounded-md
                          overflow-hidden
                          border border-gray-200
                          group
                          h-full
                        "
                      >
                        <img
                          v-if="candidato.foto"
                          :src="candidato.foto"
                          alt="avatar"
                          class="
                            object-cover object-top
                            w-20
                            h-24
                            bg-gray-100
                            flex-shrink-0
                          "
                        />
                        <div class="w-full flex flex-col justify-center">
                          <div
                            class="
                              w-full
                              px-3
                              py-1
                              bg-gradient-to-r
                              from-lime-600
                              to-lime-700
                            "
                          >
                            <span
                              class="
                                text-white
                                font-semibold
                                text-[10px]
                                tracking-wider
                                uppercase
                                block
                                truncate
                              "
                              >{{ candidato.cargo }}</span
                            >
                          </div>
                          <div class="w-full flex items-center flex-1 p-3">
                            <span
                              class="
                                text-sm
                                font-bold
                                text-gray-800
                                uppercase
                                leading-snug
                                group-hover:text-lime-700
                                transition-colors
                              "
                              >{{ candidato.nome }}</span
                            >
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Coluna Documentos -->
          <div key="col-doc" class="w-full" v-if="filteredDocuments.length > 0">
            <div class="mb-6 flex items-center">
              <h3
                class="
                  text-2xl
                  font-bold
                  text-gray-800
                  border-b-2 border-lime-600
                  pb-2
                  uppercase
                  tracking-wide
                "
              >
                Documentos
              </h3>
            </div>
            <div
              class="overflow-y-auto pr-2 custom-scrollbar"
              style="max-height: 600px"
            >
              <ul
                :key="'docs-list-' + selectedRegional"
                :class="[
                  'grid gap-3',
                  filteredDiretorios.length > 0 && filteredDocuments.length > 0
                    ? 'grid-cols-1'
                    : 'grid-cols-1 lg:grid-cols-2',
                ]"
              >
                <li
                  v-for="(document, index) in filteredDocuments"
                  :key="'doc-' + selectedRegional + '-' + index"
                  class="
                    w-full
                    shadow-sm
                    hover:shadow-md
                    transition-shadow
                    duration-200
                    rounded-md
                    border border-gray-200 border-l-4 border-l-lime-600
                    bg-white
                    flex flex-col
                    group
                  "
                >
                  <div
                    class="
                      p-3
                      flex flex-col
                      sm:flex-row
                      justify-between
                      items-start
                      sm:items-center
                      gap-3
                    "
                  >
                    <div class="flex-1 pr-2">
                      <h4
                        class="
                          text-sm
                          font-bold
                          text-gray-800
                          leading-snug
                          group-hover:text-lime-700
                          transition-colors
                        "
                      >
                        {{ document.titulo }}
                      </h4>
                    </div>
                    <div class="flex items-center gap-2 flex-shrink-0">
                      <a
                        :href="document.link"
                        target="_blank"
                        download
                        class="
                          flex
                          justify-center
                          items-center
                          w-8
                          h-8
                          rounded
                          bg-gray-100
                          hover:bg-gray-700
                          text-gray-600
                          hover:text-white
                          transition-colors
                          border border-gray-200
                          hover:border-gray-700
                        "
                        title="Download"
                      >
                        <i
                          class="fa fa-download text-sm"
                          aria-hidden="true"
                        ></i>
                      </a>
                      <a
                        :href="document.link"
                        target="_blank"
                        class="
                          flex
                          justify-center
                          items-center
                          w-8
                          h-8
                          rounded
                          bg-lime-50
                          text-lime-700
                          hover:bg-lime-700 hover:text-white
                          transition-colors
                          border border-lime-200
                          hover:border-lime-700
                        "
                        title="Visualizar"
                      >
                        <i
                          class="fa fa-file-pdf text-sm"
                          aria-hidden="true"
                        ></i>
                      </a>
                    </div>
                  </div>
                  <div
                    v-if="document.publicacao"
                    class="
                      px-4
                      py-1.5
                      text-xs
                      font-medium
                      text-gray-500
                      bg-gray-50
                      border-t border-gray-100
                      flex
                      items-center
                      gap-2
                      mt-auto
                    "
                  >
                    <i class="fa fa-calendar-alt opacity-70"></i> Publicado
                    {{ document.publicacao }}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Vue from "vue";
import services from "@/services";
import _ from "lodash";

export default Vue.extend({
  name: "RegionalInfo",
  data() {
    return {
      regionais: [],
      selectedRegional: null,
      allDiretorios: [],
      allChapas: [],
      allDocuments: [],
    };
  },
  computed: {
    filteredDiretorios() {
      if (!this.selectedRegional) return [];

      let diretorios = _.filter(
        this.allDiretorios,
        (d) => d.regionalId == this.selectedRegional
      );

      // Filtrar as chapas de cada diretorio
      let result = [];
      _.forEach(diretorios, (diretorio) => {
        let dirClone = Object.assign({}, diretorio);
        dirClone.chapas = _.filter(
          this.allChapas,
          (cargo) => cargo.diretorio == dirClone.codigo
        );
        if (dirClone.chapas.length > 0) {
          result.push(dirClone);
        }
      });
      return result;
    },
    filteredDocuments() {
      if (!this.selectedRegional) return [];
      return _.filter(
        this.allDocuments,
        (doc) => doc.regionalid == this.selectedRegional
      );
    },
  },
  async mounted() {
    try {
      // Carregar Regionais
      let resReg = await services.Eleicaonet.loadRegionais();
      this.regionais = _.orderBy(resReg.data, ["nome"]);

      // Carregar Documentos
      let resDoc = await services.Eleicaonet.loadDocuments();
      this.allDocuments = resDoc.data;

      // Carregar Candidatos (Chapas)
      let resChapas = await services.Eleicaonet.loadCandidates();
      let cargos = resChapas.data;
      cargos = _.orderBy(cargos, ["diretorio", "ordem"]);
      cargos = _.filter(
        cargos,
        (cargo) => cargo.nome !== "BRANCO" && cargo.nome !== "NULO"
      );
      _.forEach(cargos, (cargo) => {
        if (cargo.participantes) {
          try {
            cargo.candidatos = JSON.parse(cargo.participantes);
          } catch (e) {
            cargo.candidatos = [];
          }
        } else {
          cargo.candidatos = [];
        }
      });
      this.allChapas = cargos;

      // Carregar Diretórios
      let resDir = await services.Eleicaonet.loadDiretorios();
      let diretorios = resDir.data;
      this.allDiretorios = _.orderBy(diretorios, ["codigo"]);
    } catch (e) {
      console.error("Erro ao carregar dados da regional:", e);
    }
  },
});
</script>

<style scoped>
.regional-filter select {
  cursor: pointer;
}
.candidates-container::-webkit-scrollbar,
.documents-container::-webkit-scrollbar {
  width: 6px;
}
.candidates-container::-webkit-scrollbar-track,
.documents-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.candidates-container::-webkit-scrollbar-thumb,
.documents-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}
</style>
